import config from "appConfig";

const theory = `<h3 class=sectionHead>Transaural reproduction</h3>
<p>Binaural audio is a technique to create an aural perception of a three dimensional sound field for a specific listener. The foundation is the knowledge that the ears of a human function as spatial filters. The idea is to reproduce the exact same sound pressure levels at the entrance of the ear canals, which would be prevalent in a real acoustic scene [2]. As a consequence, the real perception cannot be distinguished from the virtual one. The challenge is on the one hand the correct measurement or synthesis of the sound pressure at the two ears and on the other hand the correct reproduction of these. Here we discuss only the reproduction problem. The use of headphones for reproduction is self-evident. However, loudspeakers can be used as well. As the signal reproduced by one loudspeaker arrives at both ears (direct path and crosstalk path) the question is how to achieve a clean signal condition where the right channel is only sent to the right ear, and vice versa for the left ear. The idea is to use crosstalk cancellation (CTC) before playback  [1]. In consumer audio, this technique is known as stereo dipole. It is also implemented in so-called “sound bars” for TV sets.
<h3 class=sectionHead>Crosstalk cancellation (CTC)</h3>
<p>
   The term crosstalk cancellation (CTC) describes a loudspeaker setup with special digital signal processing that can transmit audio signals directly to the ears of a listener. With conventional setups loudspeaker signals always reach both the left and the right ear and for a spatial impression sound intensity is controlled by a predefined position for each audio channel. With CTC the ipsilateral loudspeaker (closest to the corresponding ear) plays back the actual signal that is meant to be transmitted to that ear, while the contralateral loudspeaker (the one on the other side of the ear) is suppressing the crosstalk signal for the “other” ear. This can be achieved by playing back the same but phase-inverted signal to create destructive interference at the “other” ear (the ear that should not receive any sound). <a href="#" data-img-src=${config.ASSETS_URL}/03/21/theory/figure-1.svg data-img-alt="Figure 1. Iterative crosstalk compensation [2].">Figure 1</a> shows the transmission paths for a loudspeaker setup. For the left channel, the left loudspeaker is called ipsilateral and the right loudspeaker is called contralateral. The crosstalk to the right ear from the left loudspeaker is unwanted and is cancelled out by the right loudspeaker. The same must be applied for the right channel. Because of its transaural transmission features the ear signals reach only the corresponding ears, which means that the crosstalk cancellation is the only loudspeaker based audio reproduction system that can effectively play back binaural signals (which is usually only possible with headphones because of the natural channel separation and hence no crosstalk at all). A challenge for the overall quality of such a system is the high accuracy that must be used to determine the loudspeaker positions and the head position of the listener, which can be determined by a tracking system for dynamic reproduction.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/03/21/theory/figure-1.svg alt="Figure 1. Iterative crosstalk compensation [2]."><figcaption class=caption>Figure 1. Iterative crosstalk compensation [2].</figcaption></figure><figure class=figure><p><img src=${config.ASSETS_URL}/03/21/theory/figure-2.svg alt="Figure 2. Crosstalk filter design [2]."><figcaption class=caption>Figure 2. Crosstalk filter design [2].</figcaption></figure>
<table class=equation>
   <tr>
      <td style="font-size: 7px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mi>L</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>L</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>⋅</mo>
            <mstyle mathsize=2.03em>
               <mrow>
                  <mo form=prefix fence=true>[</mo>
               </mrow>
            </mstyle>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>RR</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>LL</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>⋅</mo>
                  <msub>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>RR</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>LR</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>⋅</mo>
                  <msub>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>RL</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>⋅</mo>
            <msub>
               <mrow>
                  <mi>X</mi>
               </mrow>
               <mrow>
                  <mi>L</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>RL</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>LL</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>⋅</mo>
                  <msub>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>RR</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>LR</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>⋅</mo>
                  <msub>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>RL</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>⋅</mo>
            <msub>
               <mrow>
                  <mi>X</mi>
               </mrow>
               <mrow>
                  <mi>R</mi>
               </mrow>
            </msub>
            <mstyle mathsize=2.03em>
               <mrow>
                  <mo form=prefix fence=true>]</mo>
               </mrow>
            </mstyle>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   The ear signals
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mi>R</mi>
         </mrow>
      </msub>
   </math>
   are constructed from four filters as represented in the quotients in Equation 1 and the corresponding equation for the right channel  [3]. It is important that the filter remain stable, so that the denominators in eq. 1 do not tend to zero. Actually, they become rather small in cases where the crosstalk sound at the contralateral ear is not attenuated (shadowed) sufficiently. The angular zone which provides a natural damping of the contralateral paths is thus limited and it depends on the geometric situation between the loudspeaker pair and the listener.
<h4 class=subsectionHead>Dynamic crosstalk cancellation</h4>
<p>
   The compensating method of crosstalk cancellation and of the stereo dipole are based on interferences and shadowing effects. They are thus sensitive to phase errors caused by geometric variations, particularly in translational or rotational right/left movement. If the compensating filters are calibrated for one specific position (sweet spot), offside positions of the order of magnitude of 2 cm may lead to audible effects of distorted timbre and localization as shown in <a href="#" data-img-src=${config.ASSETS_URL}/03/21/theory/figure-2.svg data-img-alt="Figure 2. Crosstalk filter design [2].">Figure 2</a>. For interactivity and free movement by the listener of the order of magnitude of metres, however, all static techniques fail. Anyway, crosstalk compensation need not be installed in a symmetric arrangement of loudspeakers and listener, as long as the exact geometric condition is known by using head tracking technology.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/03/21/theory/figure-3.svg alt="Figure 3. Mismatch by head movement"><figcaption class=caption>Figure 3. Mismatch by head movement</figcaption></figure>
<h3 class=likesectionHead>Glossary</h3>
  <dl>
    <dt>CTC
    <dd>Crosstalk Cancellation
  </dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-Bauer1961 class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Benjamin B. Bauer. “Phasor analysis of some stereophonic phenomena”. In: <span class=cmti-10>J. Acoust. Soc. Am. </span>33.11 (Nov. 1961), pp. 1536–1539. <span class=cmcsc-10><span class=small-caps>doi</span></span>: <a href="https://doi.org/10.1121/1.1908492" class=url target="_blank"><span class=cmtt-10>10.1121/1.1908492</span></a>.
   <dt id=X0-Blauert1997 class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Jens Blauert. <span class=cmti-10>Spatial Hearing. The psychophysics of human sound </span><span class=cmti-10>localization</span>. Revised. Camebridge, Massachusetts: MIT Press, 1997.
   <dt id=X0-Kirkeby1998 class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>O Kirkeby et al. “Fast deconvolution of multichannel systems using regularization”. In: <span class=cmti-10>IEEE Trans. on speech and audio processing </span>6.2 (Mar. 1998), pp. 189–194.
</dl>`;

export default theory;
